import type { GetPositionsRow } from '@morfi/backend-sdk-v2/client';
import { MarketStatus } from '@morfi/contract-sdk';

import { evaluatePortfolioPositionVisibility } from '@/components/portfolio/positions-panel/position-lifecycle-visibility';
import type { PositionCardRow } from '@/hooks/positions/user-portfolio/display-types';
import { hasQuotableOutcomeBalance } from '@/lib/market/position-visibility';

const STATUS_BONDING_CURVES_CLOSED = 5;
const STATUS_CLOSED = 6;

export function mergePositionCardRows({
  snapshotRows,
  indexedRows,
  wallet,
  market,
  outcomeMint,
}: {
  readonly snapshotRows: readonly PositionCardRow[];
  readonly indexedRows: readonly GetPositionsRow[];
  readonly wallet: string;
  readonly market?: string;
  readonly outcomeMint?: string;
}): readonly PositionCardRow[] {
  const closedRows = indexedRows.flatMap((row) => {
    const snapshot = snapshotRows.find((position) => position.positionId === row.positionId);
    // A stale closed index must not hide an already-reconciled reopen.
    if (snapshot?.tradeCount !== undefined && snapshot.tradeCount > row.tradeCount) return [];
    const isClosedScopedPosition =
      row.wallet === wallet &&
      (market === undefined || row.market === market) &&
      (outcomeMint === undefined || row.outcomeMint === outcomeMint) &&
      !hasQuotableOutcomeBalance(row) &&
      evaluatePortfolioPositionVisibility(row).isVisible;
    return isClosedScopedPosition ? [indexedClosedPositionCardRow(row)] : [];
  });
  const closedPositionIds = new Set(closedRows.map((row) => row.positionId));
  return [...snapshotRows.filter((row) => !closedPositionIds.has(row.positionId)), ...closedRows];
}

function indexedClosedPositionCardRow(row: GetPositionsRow): PositionCardRow {
  const realizedPnlD6 = signedWire(row.realizedPnlD6);
  return {
    positionId: row.positionId,
    positionType: row.positionType,
    wallet: row.wallet,
    market: row.market,
    marketTitle: row.marketTitle,
    outcomeIndex: row.outcomeIndex,
    outcomeMint: row.outcomeMint,
    outcomeLabel: row.outcomeLabel,
    outcomeImageUrl: row.outcomeImageUrl,
    outcomeImageNsfw: row.outcomeImageNsfw,
    balanceD6: row.balance,
    claimRightsBalanceD6: row.claimRightsBalanceD6,
    currentPriceD9: row.currentPriceD9,
    costBasisD6: row.costBasisD6,
    remainingCostBasisGrossD6: row.remainingCostBasisGrossD6,
    remainingCostBasisNetD6: row.remainingCostBasisNetD6,
    realizedPnlGrossD6: row.realizedPnlGrossD6,
    realizedPnlNetD6: row.realizedPnlNetD6,
    realizedPnlD6: row.realizedPnlD6,
    realizedPenaltyD6: row.realizedPenaltyD6,
    realizedCostBasisD6: row.realizedCostBasisD6,
    resultState: row.resultState,
    lifecycle: marketLifecycle(row.marketStatus),
    displayMark: {
      currentValueD6: BigInt(0),
      unrealisedPnlD6: BigInt(0),
      totalPnlD6: realizedPnlD6,
      averageEntryPriceD9: null,
      unavailableReason: realizedPnlD6 === null ? 'invalid_amount' : null,
    },
  };
}

function marketLifecycle(status: number): PositionCardRow['lifecycle'] {
  switch (status) {
    case MarketStatus.Created:
      return 'created';
    case MarketStatus.Backing:
      return 'backing';
    case MarketStatus.Trading:
      return 'trading';
    case MarketStatus.Resolved:
      return 'resolved';
    case MarketStatus.Graduated:
    case STATUS_BONDING_CURVES_CLOSED:
    case STATUS_CLOSED:
      return 'graduated';
    default:
      return null;
  }
}

function signedWire(value: string): bigint | null {
  return /^-?(?:0|[1-9]\d*)$/.test(value) ? BigInt(value) : null;
}
